import { i18n } from "./i18n";
import type { Excursion, Hotel, Place, Stop, TravelDetails, TravelMode } from "./types";

export function parseTripJson(text: string): ParsedTrip {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(i18n.t("importErrorNotJson"));
  }
  return parseTripObject(parsed);
}

export type ParsedTrip = { stops: Stop[]; tripName?: string };

export function parseTripObject(value: unknown): ParsedTrip {
  const obj = value as Record<string, unknown> | null;
  const raw = obj?.stops;
  if (!Array.isArray(raw)) {
    throw new Error(i18n.t("importErrorNotObject"));
  }
  const result: Stop[] = [];
  for (const [i, item] of raw.entries()) {
    const stop = coerceStop(item);
    if (!stop) throw new Error(i18n.t("importErrorMalformed", { n: i + 1 }));
    result.push(stop);
  }
  const tripName = typeof obj?.tripName === "string" && obj.tripName ? obj.tripName : undefined;
  return { stops: result, tripName };
}

export function coerceStop(value: unknown): Stop | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  const lat = typeof v.lat === "number" ? v.lat : NaN;
  const lon = typeof v.lon === "number" ? v.lon : NaN;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  const name = typeof v.name === "string" ? v.name : "";
  const city = typeof v.city === "string" ? v.city : name;
  if (!name && !city) return null;
  const id = typeof v.id === "string" && v.id ? v.id : crypto.randomUUID();
  const stop: Stop = { id, name: name || city, city: city || name, lat, lon };
  if (typeof v.notes === "string") stop.notes = v.notes;
  if (isISODate(v.dateFrom)) stop.dateFrom = v.dateFrom;
  if (isISODate(v.dateTo)) stop.dateTo = v.dateTo;
  if (isTravelMode(v.arriveBy)) stop.arriveBy = v.arriveBy;
  if (isTravelMode(v.departBy)) stop.departBy = v.departBy;
  const hotel = coerceHotel(v.hotel);
  if (hotel) stop.hotel = hotel;
  const arriveDetails = coerceTravelDetails(v.arriveDetails);
  if (arriveDetails) stop.arriveDetails = arriveDetails;
  const departDetails = coerceTravelDetails(v.departDetails);
  if (departDetails) stop.departDetails = departDetails;
  const places = coercePlaces(v.places);
  if (places) stop.places = places;
  const excursions = coerceExcursions(v.excursions);
  if (excursions) stop.excursions = excursions;
  return stop;
}

function coerceHotel(value: unknown): Hotel | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const v = value as Record<string, unknown>;
  const hotel: Hotel = {};
  if (typeof v.name === "string" && v.name) hotel.name = v.name;
  if (typeof v.address === "string" && v.address) hotel.address = v.address;
  if (typeof v.googleMapsUrl === "string" && v.googleMapsUrl) hotel.googleMapsUrl = v.googleMapsUrl;
  if (isISODate(v.checkIn)) hotel.checkIn = v.checkIn;
  if (isISODate(v.checkOut)) hotel.checkOut = v.checkOut;
  if (typeof v.price === "number" && Number.isFinite(v.price)) hotel.price = v.price;
  if (typeof v.currency === "string" && v.currency) hotel.currency = v.currency;
  const hasData = Object.keys(hotel).length > 0;
  return hasData ? hotel : undefined;
}

function coerceTravelDetails(value: unknown): TravelDetails | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const v = value as Record<string, unknown>;
  const details: TravelDetails = {};
  if (typeof v.carrier === "string" && v.carrier) details.carrier = v.carrier;
  if (typeof v.ticketRef === "string" && v.ticketRef) details.ticketRef = v.ticketRef;
  if (typeof v.departureTime === "string" && v.departureTime) details.departureTime = v.departureTime;
  if (typeof v.arrivalTime === "string" && v.arrivalTime) details.arrivalTime = v.arrivalTime;
  if (typeof v.price === "number" && Number.isFinite(v.price)) details.price = v.price;
  if (typeof v.currency === "string" && v.currency) details.currency = v.currency;
  const hasData = Object.keys(details).length > 0;
  return hasData ? details : undefined;
}

function coercePlaces(value: unknown): Place[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result: Place[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const p = item as Record<string, unknown>;
    if (typeof p.name !== "string" || !p.name) continue;
    const place: Place = {
      id: typeof p.id === "string" && p.id ? p.id : crypto.randomUUID(),
      name: p.name,
    };
    if (typeof p.lat === "number" && Number.isFinite(p.lat)) place.lat = p.lat;
    if (typeof p.lon === "number" && Number.isFinite(p.lon)) place.lon = p.lon;
    result.push(place);
  }
  return result.length > 0 ? result : undefined;
}

function coerceExcursions(value: unknown): Excursion[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result: Excursion[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const e = item as Record<string, unknown>;
    if (typeof e.name !== "string" || !e.name) continue;
    const now = Date.now();
    const exc: Excursion = {
      id: typeof e.id === "string" && e.id ? e.id : crypto.randomUUID(),
      name: e.name,
      createdAt: typeof e.createdAt === "number" ? e.createdAt : now,
      updatedAt: typeof e.updatedAt === "number" ? e.updatedAt : now,
    };
    if (isISODate(e.dateFrom)) exc.dateFrom = e.dateFrom;
    if (isISODate(e.dateTo)) exc.dateTo = e.dateTo;
    if (isClockTime(e.timeFrom)) exc.timeFrom = e.timeFrom;
    if (isClockTime(e.timeTo)) exc.timeTo = e.timeTo;
    // Range-checked, unlike coercePlaces above — an out-of-range pair would put a marker
    // somewhere impossible and drag the map's fitBounds with it.
    if (isLat(e.lat) && isLon(e.lon)) {
      exc.lat = e.lat;
      exc.lon = e.lon;
    }
    if (typeof e.mapsUrl === "string" && e.mapsUrl) exc.mapsUrl = e.mapsUrl;
    if (typeof e.notes === "string") exc.notes = e.notes;
    result.push(exc);
  }
  return result.length > 0 ? result : undefined;
}

function isLat(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= -90 && value <= 90;
}

function isLon(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= -180 && value <= 180;
}

function isClockTime(value: unknown): value is string {
  return typeof value === "string" && /^\d{2}:\d{2}$/.test(value);
}

function isISODate(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isTravelMode(value: unknown): value is TravelMode {
  return value === "plane" || value === "train" || value === "bus" || value === "car";
}
